import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import { footerInfo, siteInfo } from '../data/mock';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      {/* Distributeur officiel banner */}
      <div className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] py-4">
        <div className="container mx-auto px-4 text-center">
          <p className="text-lg font-bold text-white flex items-center justify-center gap-2">
            <span className="text-2xl">🇨🇭</span>
            Distributeur officiel Ecopra pour la Suisse
          </p>
        </div>
      </div>

      {/* Edison Awards Banner */}
      <div className="bg-gradient-to-r from-yellow-600 to-yellow-500 py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xl font-bold text-gray-900">
            🏆 {footerInfo.award}
          </p>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-xl font-bold mb-4">
              <span className="text-[#7CB342]">eco</span>
              <span className="text-[#00BCD4]">pra</span>
            </h3>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🇨🇭</span>
              <span className="text-white font-semibold">Suisse</span>
              <span className="text-gray-500 mx-2">+</span>
              <span className="text-2xl">🇫🇷</span>
              <span className="text-white font-semibold">France</span>
            </div>
            <p className="text-gray-400 mb-4">
              Distributeur officiel des kits Ecopra pour la Suisse. Installation professionnelle dans tout le pays.
            </p>
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-16 h-12 bg-gradient-to-r from-blue-600 via-white to-red-600 rounded shadow-md" />
                <span className="font-semibold">Made in France</span>
              </div>
              <div className="text-sm text-gray-500">
                Technologie créée en Auvergne • Depuis 2007
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#00BCD4]">Liens rapides</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/presentation" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Le Kit Ecopra
                </Link>
              </li>
              <li>
                <Link to="/boutique" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Boutique
                </Link>
              </li>
              <li>
                <Link to="/commander" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Commander
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/devenir-partenaire" className="text-gray-400 hover:text-[#00BCD4] transition-colors font-semibold">
                  🤝 Devenir partenaire
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#00BCD4]">Nos produits</h4>
            <ul className="space-y-2 text-sm">
              <li className="text-gray-400">BVA1 - Moto</li>
              <li className="text-gray-400">BVA2 - Voiture &lt; 2L</li>
              <li className="text-gray-400">BVA3 - Voiture &gt; 2L</li>
              <li className="text-gray-400">BVA4 - Utilitaires</li>
              <li className="text-gray-400">BVA5 - Agricole</li>
              <li className="text-gray-400">BVA6 - Industriel</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#00BCD4]">Contact</h4>
            <ul className="space-y-3">
              <li className="text-gray-300 font-semibold">
                {siteInfo.owner}
              </li>
              <li className="flex items-start gap-2 text-gray-400 text-sm">
                <MapPin size={18} className="mt-1 flex-shrink-0 text-[#00BCD4]" />
                <div>
                  <div className="font-semibold text-gray-300">{siteInfo.owner}</div>
                  <div>{siteInfo.address.mainCity}, {siteInfo.address.region}</div>
                </div>
              </li>
              <li className="flex items-center gap-2 text-gray-400 text-sm">
                <Mail size={18} className="flex-shrink-0 text-[#00BCD4]" />
                <a href={`mailto:${siteInfo.email}`} className="hover:text-[#00BCD4] transition-colors">
                  {siteInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-2 text-gray-400 text-sm">
                <Phone size={18} className="flex-shrink-0 text-[#00BCD4]" />
                <a href={`tel:${siteInfo.phone}`} className="hover:text-[#00BCD4] transition-colors">
                  {siteInfo.phone}
                </a>
              </li>
            </ul>
            {/* Social Media */}
            <div className="flex gap-3 mt-4">
              <a href="#" className="bg-gray-800 p-2 rounded-full hover:bg-gradient-to-r hover:from-[#7CB342] hover:to-[#00BCD4] transition-all">
                <Facebook size={18} />
              </a>
              <a href="#" className="bg-gray-800 p-2 rounded-full hover:bg-gradient-to-r hover:from-[#7CB342] hover:to-[#00BCD4] transition-all">
                <Instagram size={18} />
              </a>
              <a href="#" className="bg-gray-800 p-2 rounded-full hover:bg-gradient-to-r hover:from-[#7CB342] hover:to-[#00BCD4] transition-all">
                <Linkedin size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">© {new Date().getFullYear()} Ecopra Suisse - Tous droits réservés</p>
            <div className="flex gap-6 text-sm">
              {footerInfo.links.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-gray-400 hover:text-[#00BCD4] transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
